class BlogFiltersForm extends HTMLElement {
  static paginationParam = 'blog_page';

  constructor() {
    super();
    this.onInputChange = this.onInputChange.bind(this);
    this.onSearchInput = this.onSearchInput.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onClearAll = this.onClearAll.bind(this);
    this.onPaginationClick = this.onPaginationClick.bind(this);
    this.onApplyFilters = this.onApplyFilters.bind(this);
  }

  connectedCallback() {
    if (BlogFiltersForm.initialized) return;
    if (!this.canInitialize()) {
      this.scheduleInitialization();
      return;
    }

    BlogFiltersForm.initialized = true;
    BlogFiltersForm.initializationScheduled = false;
    BlogFiltersForm.controller = this;
    BlogFiltersForm.instances = Array.from(document.querySelectorAll('blog-filters-form'));
    BlogFiltersForm.articleCards = Array.from(document.querySelectorAll('.blog-articles__article'));
    BlogFiltersForm.articleGrid = document.querySelector('[data-blog-articles-grid]');
    BlogFiltersForm.emptyState = document.querySelector('[data-blog-empty-state]');
    BlogFiltersForm.paginationContainer = document.querySelector('[data-blog-pagination]');
    BlogFiltersForm.paginationList = document.querySelector('[data-blog-pagination-list]');
    BlogFiltersForm.paginationPrevButton = document.querySelector('[data-blog-page-prev]');
    BlogFiltersForm.paginationNextButton = document.querySelector('[data-blog-page-next]');
    BlogFiltersForm.paginationPageItems = Array.from(document.querySelectorAll('[data-blog-page-item]'));
    BlogFiltersForm.articlesPerPage = this.getArticlesPerPage();
    BlogFiltersForm.filterInputs = BlogFiltersForm.getAllFilterInputs();
    BlogFiltersForm.filterKeys = [...new Set(BlogFiltersForm.filterInputs.map((input) => this.getFilterKey(input)).filter(Boolean))];
    BlogFiltersForm.searchInputs = Array.from(document.querySelectorAll('[data-blog-title-search]'));
    BlogFiltersForm.clearButtons = Array.from(document.querySelectorAll('[data-blog-clear-all]'));

    BlogFiltersForm.instances.forEach((instance) => {
      this.bindEvents(instance);
    });
    this.applyFiltersFromUrl();
    window.addEventListener('popstate', this.onPopState);
  }

  disconnectedCallback() {
    window.removeEventListener('popstate', this.onPopState);
  }

  static getAllFilterInputs() {
    return Array.from(document.querySelectorAll('blog-filters-form input[type="checkbox"]')).filter((input) =>
      input.name.includes(':')
    );
  }

  canInitialize() {
    return Boolean(document.querySelector('[data-blog-articles-grid]')) && document.querySelectorAll('.blog-articles__article').length > 0;
  }

  scheduleInitialization() {
    if (BlogFiltersForm.initializationScheduled) return;

    BlogFiltersForm.initializationScheduled = true;
    window.requestAnimationFrame(() => {
      BlogFiltersForm.initializationScheduled = false;
      this.connectedCallback();
    });
  }

  bindEvents(root = this) {
    const form = root.querySelector('form');
    if (form && !form.dataset.blogFacetsBound) {
      form.addEventListener('change', this.onInputChange);
      form.dataset.blogFacetsBound = 'true';
    }

    root.querySelectorAll('[data-blog-title-search]').forEach((input) => {
      if (input.dataset.blogFacetsBound) return;
      input.addEventListener('input', this.onSearchInput);
      input.dataset.blogFacetsBound = 'true';
    });

    root.querySelectorAll('[data-blog-clear-all]').forEach((button) => {
      if (button.dataset.blogFacetsBound) return;
      button.addEventListener('click', this.onClearAll);
      button.dataset.blogFacetsBound = 'true';
    });

    root.querySelectorAll('[data-blog-apply-filters]').forEach((button) => {
      if (button.dataset.blogFacetsBound) return;
      button.addEventListener('click', this.onApplyFilters);
      button.dataset.blogFacetsBound = 'true';
    });

    if (BlogFiltersForm.paginationList && !BlogFiltersForm.paginationList.dataset.blogFacetsBound) {
      BlogFiltersForm.paginationList.addEventListener('click', this.onPaginationClick);
      BlogFiltersForm.paginationList.dataset.blogFacetsBound = 'true';
    }
  }

  onInputChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'checkbox' || !input.name.includes(':')) return;

    this.syncMatchingInputs(input);
    this.applyFiltersFromInputs();
  }

  onSearchInput(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    this.syncSearchInputs(input.value);
    this.applyFiltersFromInputs();
  }

  onPopState() {
    this.applyFiltersFromUrl();
  }

  onClearAll(event) {
    event.preventDefault();
    this.clearAllState();
    this.applyFiltersFromInputs();
  }

  onApplyFilters(event) {
    event.preventDefault();
    this.applyFiltersFromInputs();

    const drawerSummary = event.currentTarget.closest('.mobile-facets__wrapper')?.querySelector('summary');
    if (drawerSummary) {
      drawerSummary.click();
    }
  }

  onPaginationClick(event) {
    const directionButton = event.target.closest('[data-blog-page-prev], [data-blog-page-next]');
    if (directionButton) {
      event.preventDefault();

      const currentPage = Number(directionButton.dataset.blogPageTarget);
      if (!Number.isInteger(currentPage) || currentPage < 1) return;

      const state = {
        filters: this.getSelectedFilters(),
        searchTerm: this.getSearchValue(),
        currentPage,
      };

      const normalizedState = this.applyFilters(state);
      this.updateUrl(normalizedState);
      this.scrollToArticleGrid();
      return;
    }

    const button = event.target.closest('[data-blog-page]');
    if (!button) return;

    event.preventDefault();

    const requestedPage = Number(button.dataset.blogPage);
    if (!Number.isInteger(requestedPage) || requestedPage < 1) return;

    const state = {
      filters: this.getSelectedFilters(),
      searchTerm: this.getSearchValue(),
      currentPage: requestedPage,
    };

    const normalizedState = this.applyFilters(state);
    this.updateUrl(normalizedState);
    this.scrollToArticleGrid();
  }

  getArticlesPerPage() {
    const configuredValue = Number(BlogFiltersForm.articleGrid?.dataset.articlesPerPage);
    return Number.isInteger(configuredValue) && configuredValue > 0 ? configuredValue : 12;
  }

  getFilterKey(input) {
    return input.name.split(':')[0];
  }

  getFilterValue(input) {
    return input.value;
  }

  getFilterSlug(key) {
    return key.replace(/_/g, '-');
  }

  normalizeValue(value) {
    return value.trim().toLowerCase();
  }

  syncMatchingInputs(sourceInput) {
    const sourceName = sourceInput.name;
    const sourceValue = sourceInput.value;

    BlogFiltersForm.filterInputs.forEach((input) => {
      if (input.name === sourceName && input.value === sourceValue) {
        input.checked = sourceInput.checked;
      }
    });
  }

  clearAllInputs() {
    BlogFiltersForm.filterInputs.forEach((input) => {
      input.checked = false;
    });
  }

  clearAllState() {
    this.clearAllInputs();
    this.syncSearchInputs('');
  }

  syncSearchInputs(value) {
    BlogFiltersForm.searchInputs.forEach((input) => {
      input.value = value;
    });
  }

  getSearchValue() {
    const input = BlogFiltersForm.searchInputs.find((searchInput) => searchInput.value.trim() !== '');
    return input ? input.value.trim() : '';
  }

  getSelectedFilters() {
    const filters = {};

    BlogFiltersForm.filterInputs.forEach((input) => {
      if (!input.checked) return;

      const key = this.getFilterKey(input);
      const value = this.getFilterValue(input);

      if (!filters[key]) filters[key] = [];
      if (!filters[key].includes(value)) filters[key].push(value);
    });

    return filters;
  }

  getStateFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const state = {
      filters: {},
      searchTerm: params.get('title') ? params.get('title').trim() : '',
      currentPage: this.getPageFromParams(params),
    };

    BlogFiltersForm.filterKeys.forEach((key) => {
      const rawValue = params.get(key);
      if (!rawValue) return;

      state.filters[key] = rawValue
        .split(',')
        .map((value) => value.trim())
        .filter((value, index, collection) => value && collection.indexOf(value) === index);
    });

    return state;
  }

  getPageFromParams(params) {
    const page = Number(params.get(BlogFiltersForm.paginationParam));
    return Number.isInteger(page) && page > 0 ? page : 1;
  }

  setInputsFromState(filters, searchTerm) {
    this.clearAllInputs();
    this.syncSearchInputs(searchTerm);

    Object.entries(filters).forEach(([key, values]) => {
      BlogFiltersForm.filterInputs.forEach((input) => {
        if (this.getFilterKey(input) !== key) return;
        input.checked = values.includes(this.getFilterValue(input));
      });
    });
  }

  applyFiltersFromInputs() {
    const state = {
      filters: this.getSelectedFilters(),
      searchTerm: this.getSearchValue(),
      currentPage: 1,
    };

    const normalizedState = this.applyFilters(state);
    this.updateUrl(normalizedState);
  }

  applyFiltersFromUrl() {
    const state = this.getStateFromUrl();
    this.setInputsFromState(state.filters, state.searchTerm);
    const normalizedState = this.applyFilters(state);

    if (normalizedState.currentPage !== state.currentPage) {
      this.updateUrl(normalizedState, true);
    }
  }

  applyFilters(state) {
    const { filters, searchTerm } = state;
    const normalizedSearchTerm = this.normalizeValue(searchTerm);
    const matchedCards = [];

    BlogFiltersForm.articleCards.forEach((articleCard) => {
      const matchesFilters = Object.entries(filters).every(([key, values]) => {
        if (!values.length) return true;

        const attributeValue = articleCard.getAttribute(`data-${this.getFilterSlug(key)}`);
        if (!attributeValue) return false;

        const articleValues = attributeValue
          .split(',')
          .map((value) => this.normalizeValue(value))
          .filter(Boolean);

        return values.some((value) => articleValues.includes(this.normalizeValue(value)));
      });
      const matchesTitle =
        !normalizedSearchTerm || this.getArticleTitle(articleCard).includes(normalizedSearchTerm);
      const matches = matchesFilters && matchesTitle;

      if (matches) {
        matchedCards.push(articleCard);
      }
    });

    const totalPages = Math.max(1, Math.ceil(matchedCards.length / BlogFiltersForm.articlesPerPage));
    const currentPage = Math.min(Math.max(state.currentPage || 1, 1), totalPages);
    const startIndex = (currentPage - 1) * BlogFiltersForm.articlesPerPage;
    const endIndex = startIndex + BlogFiltersForm.articlesPerPage;

    BlogFiltersForm.articleCards.forEach((articleCard) => {
      articleCard.hidden = true;
    });

    matchedCards.slice(startIndex, endIndex).forEach((articleCard) => {
      articleCard.hidden = false;
    });

    if (BlogFiltersForm.emptyState) {
      BlogFiltersForm.emptyState.hidden = matchedCards.length !== 0;
    }

    const normalizedState = {
      filters,
      searchTerm,
      currentPage,
      totalPages,
      totalMatches: matchedCards.length,
    };

    this.renderPagination(normalizedState);
    this.toggleClearButtons(normalizedState);
    return normalizedState;
  }

  updateUrl(state, replace = false) {
    const { filters, searchTerm } = state;
    const params = new URLSearchParams(window.location.search);

    BlogFiltersForm.filterKeys.forEach((key) => {
      params.delete(key);
    });
    params.delete('title');
    params.delete(BlogFiltersForm.paginationParam);

    Object.entries(filters).forEach(([key, values]) => {
      if (!values.length) return;
      params.set(key, values.join(','));
    });

    if (searchTerm) {
      params.set('title', searchTerm);
    }

    if (state.currentPage > 1) {
      params.set(BlogFiltersForm.paginationParam, String(state.currentPage));
    }

    const queryString = params.toString();
    const nextUrl = queryString ? `${window.location.pathname}?${queryString}` : window.location.pathname;
    const currentUrl = `${window.location.pathname}${window.location.search}`;
    if (currentUrl === nextUrl) return;

    const historyMethod = replace ? 'replaceState' : 'pushState';
    window.history[historyMethod](state, '', nextUrl);
  }

  getArticleTitle(articleCard) {
    if (articleCard.dataset.blogTitle) {
      return articleCard.dataset.blogTitle;
    }

    const titleElement = articleCard.querySelector('.card__heading a, .card__heading');
    const title = this.normalizeValue(titleElement ? titleElement.textContent : '');
    articleCard.dataset.blogTitle = title;
    return title;
  }

  toggleClearButtons(state) {
    const hasActiveFilters =
      Boolean(state.searchTerm) || Object.values(state.filters).some((values) => values.length > 0);

    BlogFiltersForm.clearButtons.forEach((button) => {
      button.classList.toggle('hidden', !hasActiveFilters);
    });
  }

  renderPagination(state) {
    if (!BlogFiltersForm.paginationContainer || !BlogFiltersForm.paginationList) return;

    if (state.totalMatches === 0 || state.totalPages <= 1) {
      BlogFiltersForm.paginationContainer.hidden = true;
      return;
    }

    BlogFiltersForm.paginationPageItems.forEach((item, index) => {
      const pageNumber = index + 1;
      const pageButton = item.querySelector('[data-blog-page]');
      const currentLabel = item.querySelector('[data-blog-page-current]');

      item.classList.toggle('hidden', pageNumber > state.totalPages);

      if (pageButton) {
        pageButton.dataset.blogPage = String(pageNumber);
      }

      if (currentLabel) {
        currentLabel.textContent = String(pageNumber);
      }

      const isCurrent = pageNumber === state.currentPage;

      if (pageButton) {
        pageButton.classList.toggle('hidden', isCurrent);
      }

      if (currentLabel) {
        currentLabel.classList.toggle('hidden', !isCurrent);
      }
    });

    if (BlogFiltersForm.paginationPrevButton) {
      BlogFiltersForm.paginationPrevButton.classList.toggle('hidden', state.currentPage <= 1);
      BlogFiltersForm.paginationPrevButton.dataset.blogPageTarget = String(state.currentPage - 1);
    }

    if (BlogFiltersForm.paginationNextButton) {
      BlogFiltersForm.paginationNextButton.classList.toggle('hidden', state.currentPage >= state.totalPages);
      BlogFiltersForm.paginationNextButton.dataset.blogPageTarget = String(state.currentPage + 1);
    }

    BlogFiltersForm.paginationContainer.hidden = false;
  }

  scrollToArticleGrid() {
    if (!BlogFiltersForm.articleGrid) return;
    BlogFiltersForm.articleGrid.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
}

BlogFiltersForm.initialized = false;
BlogFiltersForm.initializationScheduled = false;
BlogFiltersForm.instances = [];
BlogFiltersForm.articleCards = [];
BlogFiltersForm.articleGrid = null;
BlogFiltersForm.emptyState = null;
BlogFiltersForm.paginationContainer = null;
BlogFiltersForm.paginationList = null;
BlogFiltersForm.paginationPrevButton = null;
BlogFiltersForm.paginationNextButton = null;
BlogFiltersForm.paginationPageItems = [];
BlogFiltersForm.articlesPerPage = 12;
BlogFiltersForm.filterInputs = [];
BlogFiltersForm.filterKeys = [];
BlogFiltersForm.searchInputs = [];
BlogFiltersForm.clearButtons = [];

if (!customElements.get('blog-filters-form')) {
  customElements.define('blog-filters-form', BlogFiltersForm);
}

class BlogFacetRemove extends HTMLElement {
  constructor() {
    super();
    this.onClick = this.onClick.bind(this);
    this.onKeyUp = this.onKeyUp.bind(this);
  }

  connectedCallback() {
    this.link = this.querySelector('a');
    if (!this.link || this.link.dataset.blogFacetRemoveBound) return;

    this.link.setAttribute('role', 'button');
    this.link.addEventListener('click', this.onClick);
    this.link.addEventListener('keyup', this.onKeyUp);
    this.link.dataset.blogFacetRemoveBound = 'true';
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'SPACE') return;
    event.preventDefault();
    this.onClick(event);
  }

  onClick(event) {
    event.preventDefault();

    const formElement = document.querySelector('blog-filters-form');
    if (!formElement) return;

    formElement.clearAllState();
    formElement.applyFiltersFromInputs();
  }
}

if (!customElements.get('facet-remove')) {
  customElements.define('facet-remove', BlogFacetRemove);
}
